import { v4 as uuidv4 } from 'uuid';
import { db } from './schema';
import type { LoanEvent, LoanEventType } from '@/types';

/**
 * ============================================================================
 * EVENTS REPOSITORY
 * ============================================================================
 *
 * Unified read/write API over all six event-type tables, so the
 * Timeline UI and event forms work with a single `LoanEvent` union rather
 * than knowing about six separate Dexie tables.
 *
 * EDITING MODEL (spec: "Event editing/soft deletion" is a Phase 1
 * requirement; spec §11 fixed decision: events are append-only in
 * principle, corrections are new events, not silent mutation):
 *
 *   - `softDeleteEvent()` sets `supersededAt`/`supersededReason` on the
 *     existing row. The row is NEVER removed from IndexedDB.
 *   - `editEvent()` is implemented as: soft-delete the existing row (with
 *     reason "edited"), then insert a brand-new row with a new id
 *     carrying the corrected fields. This preserves a genuine audit
 *     trail — you can always see the pre-edit values by including
 *     superseded rows in a query.
 *   - All "list" queries default to EXCLUDING superseded rows, so the
 *     rest of the app (and, later, the calculation engine) naturally
 *     only sees the current, active event set unless it explicitly asks
 *     for history.
 */

function nowIso(): string {
  return new Date().toISOString();
}

/** Maps each event type to its Dexie table, so generic helpers can dispatch. */
function tableFor(type: LoanEventType) {
  switch (type) {
    case 'disbursement':
      return db.disbursementEvents;
    case 'rate_change':
      return db.rateChangeEvents;
    case 'prepayment':
      return db.prepaymentEvents;
    case 'balance_correction':
      return db.balanceCorrectionEvents;
    case 'charge':
      return db.chargeEvents;
    case 'emi_date_change':
      return db.emiDateChangeEvents;
  }
}

const ALL_EVENT_TYPES: LoanEventType[] = [
  'disbursement',
  'rate_change',
  'prepayment',
  'balance_correction',
  'charge',
  'emi_date_change',
];

/**
 * Inserts a brand-new event. Callers pass every field except the audit
 * fields (id/createdAt/updatedAt), which this function fills in.
 */
export async function addEvent<T extends LoanEvent>(
  event: Omit<T, 'id' | 'createdAt' | 'updatedAt'>
): Promise<T> {
  const timestamp = nowIso();
  const fullEvent = {
    ...event,
    id: uuidv4(),
    createdAt: timestamp,
    updatedAt: timestamp,
  } as T;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  await (tableFor(event.type) as any).add(fullEvent);
  return fullEvent;
}

/**
 * Soft-deletes a single event row. The row remains in storage with
 * supersededAt/supersededReason set; it will be excluded from default
 * list queries from this point on.
 *
 * NOTE: `tableFor()` returns a union of six distinct EntityTable<X,'id'>
 * types. Dexie 4's `.update()` typing (UpdateSpec<T>) does not always
 * distribute cleanly across such a union, so we cast the table reference
 * itself to a minimal shape for this call. This is safe: every one of
 * the six tables genuinely accepts these two fields (they're declared
 * on AuditFields, which every event type extends).
 */
export async function softDeleteEvent(type: LoanEventType, id: string, reason?: string): Promise<void> {
  const table = tableFor(type) as unknown as {
    update: (id: string, changes: Record<string, unknown>) => Promise<number>;
  };
  await table.update(id, {
    supersededAt: nowIso(),
    supersededReason: reason ?? 'Deleted by user',
    updatedAt: nowIso(),
  });
}

/**
 * Edits an event by soft-deleting the old row and inserting a new one
 * with the corrected fields. Returns the new event. This is the ONLY
 * sanctioned way to change an existing event's data — never call
 * `table.update()` directly on a field that affects the loan's
 * calculation (id/type/loanId excluded, obviously, since those can't
 * change across an edit).
 */
export async function editEvent<T extends LoanEvent>(
  original: T,
  patch: Partial<Omit<T, 'id' | 'type' | 'loanId' | 'createdAt' | 'updatedAt' | 'supersededAt' | 'supersededReason'>>
): Promise<T> {
  await softDeleteEvent(original.type, original.id, 'Edited by user (superseded by corrected entry)');

  const timestamp = nowIso();
  const { id: _oldId, createdAt: _oldCreatedAt, updatedAt: _oldUpdatedAt, supersededAt: _s1, supersededReason: _s2, ...rest } =
    original;
  const newEvent = {
    ...rest,
    ...patch,
    id: uuidv4(),
    createdAt: timestamp, // the new row's own creation time
    updatedAt: timestamp,
  } as T;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  await (tableFor(original.type) as any).add(newEvent);
  return newEvent;
}

export interface ListEventsOptions {
  loanId: string;
  includeSuperseded?: boolean;
  types?: LoanEventType[];
}

/**
 * Returns every active (non-superseded, unless requested) event for the
 * given loan, across all event types, sorted by effectiveDate descending
 * (most recent first) — the natural order for the reverse-chronological
 * Timeline. Ties on effectiveDate are broken by createdAt descending, so
 * events entered later show above ones entered earlier for the same
 * effective date, which is a reasonable, deterministic default.
 */
export async function listEvents(options: ListEventsOptions): Promise<LoanEvent[]> {
  const typesToQuery = options.types ?? ALL_EVENT_TYPES;

  const resultsPerType = await Promise.all(
    typesToQuery.map(async (type) => {
      const table = tableFor(type);
      const rows = await table.where('loanId').equals(options.loanId).toArray();
      return options.includeSuperseded ? rows : rows.filter((r) => !r.supersededAt);
    })
  );

  const merged = resultsPerType.flat() as LoanEvent[];

  merged.sort((a, b) => {
    if (a.effectiveDate !== b.effectiveDate) {
      return a.effectiveDate < b.effectiveDate ? 1 : -1; // descending
    }
    return a.createdAt < b.createdAt ? 1 : -1; // descending
  });

  return merged;
}

/**
 * Returns the full history (including superseded rows) for a single
 * event type, useful for an "edit history" detail view later. Sorted
 * oldest-first so the sequence of corrections reads naturally.
 */
export async function getEventHistory(type: LoanEventType, loanId: string): Promise<LoanEvent[]> {
  const table = tableFor(type);
  const rows = await table.where('loanId').equals(loanId).toArray();
  return (rows as LoanEvent[]).sort((a, b) => (a.createdAt > b.createdAt ? 1 : -1));
}
